import { api } from "@/lib/api";
import { ChallengeContent, StudioContentStatus } from "../schema";

export const CONTENT_CHALLENGE = "/content-challenges";

export const ContentChallengeService = {
  async findAll(filters?: {
    search?: string;
    status?: StudioContentStatus;
    categoryId?: string;
    authorCoachId?: string;
    tagId?: string;
    language?: string;
    page?: number;
    limit?: number;
    includeArchived?: boolean;
  }): Promise<{ items: ChallengeContent[]; pagination: any }> {
    const params = new URLSearchParams();
    if (filters?.search) params.set("search", filters.search);
    if (filters?.status) params.set("status", filters.status);
    if (filters?.categoryId) params.set("categoryId", filters.categoryId);
    if (filters?.authorCoachId) params.set("coachId", filters.authorCoachId);
    if (filters?.tagId) params.set("tagId", filters.tagId);
    if (filters?.language) params.set("languageCode", filters.language);
    if (filters?.page) params.set("page", filters.page.toString());
    if (filters?.limit) params.set("limit", filters.limit.toString());
    if (filters?.includeArchived !== undefined)
      params.set("includeArchived", String(filters.includeArchived));

    const res = await api.get<{
      data?: ChallengeContent[];
      items?: ChallengeContent[];
      total?: number;
      page?: number;
      limit?: number;
      pagination?: any;
    }>(`${CONTENT_CHALLENGE}?${params.toString()}`);

    if (res?.items && res?.pagination) {
      return res as { items: ChallengeContent[]; pagination: any };
    }

    const items = res?.items ?? res?.data ?? [];
    const total = res?.total ?? items.length;
    const page = res?.page ?? 1;
    const limit = res?.limit ?? 10;

    return {
      items: Array.isArray(items) ? items : [],
      pagination: {
        total,
        page,
        limit,
        totalPages: Math.ceil(total / limit) || 1,
      },
    };
  },

  async findOne(id: string): Promise<ChallengeContent> {
    return api.get<ChallengeContent>(`${CONTENT_CHALLENGE}/${id}`);
  },

  async findByVersionId(versionId: string): Promise<any> {
    return api.get<any>(`${CONTENT_CHALLENGE}/version/${versionId}`);
  },

  async create(
    payload: Partial<ChallengeContent> | Record<string, unknown>,
  ): Promise<ChallengeContent> {
    return api.post<ChallengeContent>(CONTENT_CHALLENGE, payload);
  },

  async update(
    id: string,
    payload: Partial<ChallengeContent>,
  ): Promise<ChallengeContent> {
    return api.put<ChallengeContent>(`${CONTENT_CHALLENGE}/${id}`, payload);
  },

  async submitForReview(id: string): Promise<any> {
    return api.post(`${CONTENT_CHALLENGE}/${id}/submit`, {});
  },

  async createVersion(
    id: string,
    dto?: { languages?: any[]; internalNotes?: string },
  ): Promise<{ id: string; version: number }> {
    return api.post(`${CONTENT_CHALLENGE}/${id}/version`, dto ?? {});
  },

  async addVersionLanguages(
    challengeId: string,
    versionId: string,
    languages: Array<{
      languageCode: string;
      isDefault: boolean;
      localizedTitle: string;
      localizedDescription?: string | null;
      audioContentId?: string | null;
      audioFileUrl?: string | null;
      audioFileName?: string | null;
      durationSeconds?: number | null;
      thumbnailUrl?: string | null;
    }>,
  ): Promise<{ count: number }> {
    return api.post(
      `${CONTENT_CHALLENGE}/${challengeId}/version/${versionId}/languages`,
      { languages },
    );
  },

  async updateVersionLanguage(
    challengeId: string,
    versionId: string,
    languageCode: string,
    dto: Partial<{
      localizedTitle: string;
      localizedDescription: string | null;
      audioContentId: string | null;
      audioFileUrl: string | null;
      audioFileName: string | null;
      durationSeconds: number | null;
      thumbnailUrl: string | null;
      coachId: string | null;
    }>,
  ): Promise<ChallengeContent> {
    return api.put<ChallengeContent>(
      `${CONTENT_CHALLENGE}/${challengeId}/version/${versionId}/language/${languageCode}`,
      dto,
    );
  },

  async deleteVersionLanguage(languageId: string): Promise<void> {
    return api.delete(`${CONTENT_CHALLENGE}/versions/languages/${languageId}`);
  },

  async archive(id: string): Promise<ChallengeContent> {
    return api.delete<ChallengeContent>(`${CONTENT_CHALLENGE}/${id}`);
  },

  async restore(id: string): Promise<ChallengeContent> {
    return api.post<ChallengeContent>(`${CONTENT_CHALLENGE}/${id}/restore`, {});
  },

  async assignCategory(
    id: string,
    data: { categoryId: string | null },
  ): Promise<ChallengeContent> {
    return api.patch<ChallengeContent>(
      `${CONTENT_CHALLENGE}/${id}/assign-category`,
      data,
    );
  },
};
